// SPDX-License-Identifier: AGPL-3.0-or-later

import {ATTACHMENT_MAX_WIDTH} from '@app/features/messaging/utils/MediaDimensionConfig';
import type {MessageAttachment} from '@fluxer/schema/src/domains/message/MessageResponseSchemas';

export const ATTACHMENT_CARD_WIDTH = 400;

// Prefix-matched so formats the browser gains later (jxl, heic, …) land in the mosaic too, but
// SVG is excluded: it has no intrinsic raster size, so it must not be sized from a fallback.
const NON_RASTER_IMAGE_TYPES = ['image/svg+xml'];

export function isRasterImageType(contentType?: string | null): boolean {
	if (!contentType?.startsWith('image/')) return false;
	return !NON_RASTER_IMAGE_TYPES.includes(contentType);
}

function isVideoType(contentType?: string): boolean {
	return contentType?.startsWith('video/') ?? false;
}

export const hasRenderableDimensions = (attachment: Pick<MessageAttachment, 'width' | 'height'>): boolean =>
	typeof attachment.width === 'number' &&
	attachment.width > 0 &&
	typeof attachment.height === 'number' &&
	attachment.height > 0;

export function getInlineMediaNaturalSize(attachment: Pick<MessageAttachment, 'width' | 'height'>): {
	width: number;
	height: number;
} {
	if (hasRenderableDimensions(attachment)) {
		return {width: attachment.width!, height: attachment.height!};
	}
	return {width: ATTACHMENT_MAX_WIDTH, height: ATTACHMENT_MAX_WIDTH};
}

export const isMediaAttachment = (attachment: MessageAttachment): boolean => {
	if (isRasterImageType(attachment.content_type)) return true;
	return hasRenderableDimensions(attachment) && isVideoType(attachment.content_type);
};

export function splitMediaAndFileAttachments(attachments: ReadonlyArray<MessageAttachment>): {
	mediaAttachments: Array<MessageAttachment>;
	fileAttachments: Array<MessageAttachment>;
} {
	const mediaAttachments: Array<MessageAttachment> = [];
	const fileAttachments: Array<MessageAttachment> = [];
	for (const attachment of attachments) {
		if (isMediaAttachment(attachment)) {
			mediaAttachments.push(attachment);
		} else {
			fileAttachments.push(attachment);
		}
	}
	return {mediaAttachments, fileAttachments};
}
